import Badge from '@cobliteam/colib-ui/Badge';
import Button from '@cobliteam/colib-ui/Button';
import { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@cobliteam/colib-ui/Dialog';
import DialogCloseIcon from '@cobliteam/colib-ui/Icon';
import Typography from '@cobliteam/colib-ui/Typography';
import type { MaturityItemId } from '@cobliteam/hooks/src/useBasicValueMaturity/basicValueMaturityModel';
import { useTranslation } from '@cobliteam/i18n';
import i18nConfig from '@cobliteam/i18n/src/config/i18next.config';
import { type LucideIcon, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
export type BasicValueCriticality = 'critical' | 'urgent' | 'important' | 'suggestive';
export interface BasicValueStep {
  id: MaturityItemId;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  /** Short, punchy sentence on the advantage of completing this step. */
  benefit?: string;
  /** Optional tag derived from the AI recommendation criticality. */
  criticality?: BasicValueCriticality;
  criticalityLabel?: string;
  /** Optional bullet reasons coming from the AI recommendation. */
  reasons?: string[];
}
export interface BasicValueStepHelpers {
  goNext: () => void;
  goBack: () => void;
  goToStep: (index: number) => void;
  close: () => void;
  isFirst: boolean;
  isLast: boolean;
}
interface DialogBasicValueOnboardingViewProps {
  steps: BasicValueStep[];
  onClose: () => void;
  onStepChange?: (featureId: MaturityItemId, stepIndex: number) => void;
  /** Renders the primary action(s) for the current step in the footer (inline create, navigate, dismiss…). */
  renderStepAction: (step: BasicValueStep, helpers: BasicValueStepHelpers) => ReactNode;
  /** Optional extra content rendered in the dialog body (e.g. list of vehicles without a driver). */
  renderStepBody?: (step: BasicValueStep, helpers: BasicValueStepHelpers) => ReactNode;
  /** Renders the maturity track for the current step at the top of the body. */
  renderTrack?: (step: BasicValueStep) => ReactNode;
  /** Step to open at (e.g. when launched from the Basic Value page for a specific item). */
  initialStepId?: MaturityItemId;
  /** "Não quero mais ver isso" — when provided, shows the snooze button in the footer. */
  onDismissSnooze?: () => void;
  /**
   * When true, a side panel (e.g. the checklist drawer) is open on the right. The dialog renders
   * as a left-anchored, overlay-less panel so the drawer keeps focus and both stay visible.
   */
  sidePanelOpen?: boolean;
}
const criticalityColor: Record<BasicValueCriticality, 'destructive' | 'warning' | 'alert' | 'secondary'> = {
  critical: 'destructive',
  urgent: 'warning',
  important: 'alert',
  suggestive: 'secondary',
};
export const DialogBasicValueOnboardingView = ({
  steps,
  onClose,
  onStepChange,
  renderStepAction,
  renderStepBody,
  renderTrack,
  initialStepId,
  onDismissSnooze,
  sidePanelOpen,
}: DialogBasicValueOnboardingViewProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  // The current step is tracked by ID, never by position: `steps` comes from live entity counts and
  // shrinks as they resolve (a satisfied step leaves the list), so a stored index silently slid onto a
  // NEIGHBOURING step mid-session and the footer action navigated to the wrong feature (AE-2444).
  const [currentStepId, setCurrentStepId] = useState(initialStepId);
  const total = steps.length;
  const pinnedIndex = steps.findIndex((step) => step.id === currentStepId);
  // The pinned step is missing from the list while its count is still loading (it isn't in there yet)
  // and once it's resolved (it left). Both fall back to the first step — the list is in priority order,
  // so that's the next thing to configure — and the pin snaps back as soon as the step shows up.
  const safeIndex = pinnedIndex === -1 ? 0 : pinnedIndex;
  const current = steps[safeIndex];
  if (!current) return null;
  const goToStep = (nextIndex: number) => {
    const clamped = Math.max(0, Math.min(nextIndex, total - 1));
    setCurrentStepId(steps[clamped].id);
    onStepChange?.(steps[clamped].id, clamped);
  };
  const helpers: BasicValueStepHelpers = {
    goNext: () => goToStep(safeIndex + 1),
    goBack: () => goToStep(safeIndex - 1),
    goToStep,
    close: onClose,
    isFirst: safeIndex === 0,
    isLast: safeIndex === total - 1,
  };
  const Icon = current.icon;
  const bodyContent = (
    <div className='flex flex-col gap-4'>
      {/* Stage track first: it orients the user on which setup stage they're in (and which step within
          it) before any step-specific copy. The framing phrase lives in the dialog title above. */}
      {renderTrack && <div>{renderTrack(current)}</div>}
      <div className='flex flex-col gap-4 border-border border-t pt-4'>
        {current.criticality && current.criticalityLabel && (
          <div className='flex items-center gap-2'>
            <Badge variant={criticalityColor[current.criticality]} size='sm'>
              {current.criticalityLabel}
            </Badge>
          </div>
        )}
        <div className='flex items-start gap-4'>
          <div className='flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-link'>
            <Icon size={24} aria-hidden />
          </div>
          <div className='flex flex-col gap-2'>
            <Typography variant='title' className='gap-1 break-words font-semibold text-foreground'>
              {current.title}
            </Typography>
            <Typography variant='body' className='gap-1 break-words text-foreground text-muted-foreground'>
              {current.subtitle}
            </Typography>
            {current.reasons && current.reasons.length > 0 && (
              <ul className='flex list-disc flex-col gap-1 pl-4'>
                {current.reasons.map((reason) => (
                  <li key={reason}>
                    <Typography variant='caption' className='gap-1 break-words text-foreground text-muted-foreground'>
                      {reason}
                    </Typography>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {/* Benefit spans the full width (left-aligned, no icon) so the value prop reads as its own line. */}
        {current.benefit && <div className='rounded-md bg-primary/10 px-3 py-2 text-link text-sm'>{current.benefit}</div>}
        {renderStepBody?.(current, helpers)}
      </div>
    </div>
  );
  const footerContent = (
    <>
      {onDismissSnooze ? (
        <Button variant='ghost' onClick={onDismissSnooze}>
          {t('dismiss_snooze')}
        </Button>
      ) : (
        <span />
      )}
      <div className='flex gap-2'>
        {!helpers.isFirst && (
          <Button variant='outline' onClick={helpers.goBack}>
            {t('back')}
          </Button>
        )}
        {!helpers.isLast && (
          <Button variant='outline' onClick={helpers.goNext}>
            {t('next')}
          </Button>
        )}
        {renderStepAction(current, helpers)}
      </div>
    </>
  );

  // Side-panel mode: a side drawer (e.g. checklist) is open on the right. Render the dialog as a
  // left-anchored, overlay-less panel so the drawer keeps focus and both stay visible side by side.
  if (sidePanelOpen) {
    return (
      <div className='fade-in slide-in-from-left-4 fixed top-1/2 left-[22%] z-[10000] w-[460px] max-w-[calc(100vw-2rem)] -translate-y-1/2 animate-in duration-300'>
        <div className='flex max-h-[calc(100vh-2rem)] flex-col gap-4 overflow-hidden rounded-xl border border-border border-solid bg-card p-6 text-card-foreground shadow-lg'>
          <div className='flex items-start justify-between gap-2'>
            <Typography variant='title' className='gap-1 break-words font-semibold text-foreground'>
              {t('intro')}
            </Typography>
            <button type='button' onClick={onClose} aria-label={t('close_aria')} className='-mr-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground'>
              <X size={18} />
            </button>
          </div>
          <div className='-mr-2 overflow-y-auto pr-2'>{bodyContent}</div>
          <div className='flex items-center justify-between gap-2 border-border border-t pt-4'>{footerContent}</div>
        </div>
      </div>
    );
  }
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='bg-card text-card-foreground' size='md' showCloseButton={false}>
        <DialogHeader className='flex-row items-center justify-between'>
          <DialogTitle className='text-foreground'>{t('intro')}</DialogTitle>
          <DialogDescription className='sr-only'>{`${current.title} — ${current.subtitle}`}</DialogDescription>
          <DialogClose
            aria-label={i18nConfig.t('common:buttons.close')}
            data-testid={'dialog-close'}
            render={
              <Button variant='ghost' size='icon-sm' className='text-muted-foreground'>
                <DialogCloseIcon name='x' size='sm' />
              </Button>
            }
          />
        </DialogHeader>
        <DialogBody>{bodyContent}</DialogBody>
        <DialogFooter className='flex items-center justify-between gap-2'>{footerContent}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
